/* RESPONSIBLE TEAM: team-reporting */

import type {
  ChartSchema,
  ChartDefinition,
  Timezone,
} from '@/features/report-snapshots/api/schema';
import { I18n } from '@lingui/core';
import { formatDayOfWeekShort } from '@/features/report-snapshots/formatters/timestamp';
import { Chart } from '@highcharts/react';
import DefaultHeatmapChartConfig from '@/features/report-snapshots/components/charts/default-heatmap-chart-config';
import {
  extractNumericValue,
  isDuration,
  isPercent,
} from '@/features/report-snapshots/utils/chart-data-helpers';
import { makeHeatmapTooltipFormatter } from '@/features/report-snapshots/formatters/tooltip-formatter';
import { formatHeatmapLegendLabels } from '@/features/report-snapshots/formatters/legend-labels';
import type {
  ColorAxisOptions,
  DataLabelsFormatterCallbackFunction,
  TooltipFormatterCallbackFunction,
} from 'highcharts';
import { ChartTitle } from '@/features/report-snapshots/components/charts/chart-title';
import { makeDefaultDataLabelFormatter } from '@/features/report-snapshots/formatters/datalabel-formatter';
import { getHeatmapColorStops } from '@/features/report-snapshots/utils/heatmap-colors';

import Highcharts from 'highcharts';
import 'highcharts/modules/heatmap';

interface HeatmapChartProps {
  chartSchema: ChartSchema;
  chartDefinition: ChartDefinition;
  timezone: Timezone;
  i18n: I18n;
  onReady?: (series: HeatmapData[], xAxisLabels: string[], yAxisLabels: string[]) => void;
}

export interface HeatmapData {
  x: number;
  y: number;
  value: number | null;
}

function getSeries(schema: ChartSchema): HeatmapData[] {
  const data: HeatmapData[] = [];

  schema.values.forEach((row, yIndex) => {
    Object.keys(row).forEach((key) => {
      const columnIndex = Number(key);
      if (columnIndex === 0) return; // Skip label column

      const xIndex = columnIndex - 1;
      const cellValue = row[key];

      const numericValue = extractNumericValue(cellValue);

      data.push({
        x: xIndex,
        y: schema.values.length - (yIndex + 1),
        value: numericValue,
      });
    });
  });

  return data;
}

interface HeatmapLabels {
  xAxisLabels: string[];
  yAxisLabels: string[];
}

function getLabels(schema: ChartSchema, i18n: I18n): HeatmapLabels {
  const yAxisLabels = schema.values.map((value) => {
    const label = value['0'];
    if (label === null) return '';

    if (typeof label === 'string') return label;
    if (typeof label === 'number') {
      return formatDayOfWeekShort(label, i18n);
    }
    return '';
  });

  const xAxisLabels = schema.headers
    ? schema.headers.slice(1).map((header) => String(header.value))
    : [];

  return { xAxisLabels, yAxisLabels: yAxisLabels.reverse() };
}

function setColorAxis(
  config: DefaultHeatmapChartConfig,
  chartSchema: ChartSchema,
  chartDefinition: ChartDefinition,
) {
  if (config.config.colorAxis) {
    (config.config.colorAxis as ColorAxisOptions).stops = getHeatmapColorStops(chartDefinition);
    (config.config.colorAxis as ColorAxisOptions).tickPositioner = function () {
      return [this.min, this.max] as number[];
    };

    (config.config.colorAxis as ColorAxisOptions).labels = {
      formatter: formatHeatmapLegendLabels(chartSchema),
    };
  }
}

function setDataLabelFormatter(
  config: DefaultHeatmapChartConfig,
  isPercent: boolean,
  isDuration: boolean,
) {
  if (config.config.plotOptions?.series?.dataLabels) {
    config.config.plotOptions.series.dataLabels = {
      enabled: true,
      formatter: makeDefaultDataLabelFormatter(
        false,
        isPercent,
        isDuration,
        0,
      ) as DataLabelsFormatterCallbackFunction,
    };
  }
}

function setTooltipFormatter(
  config: DefaultHeatmapChartConfig,
  yAxisLabels: string[],
  isPercentValue: boolean,
  isDurationValue: boolean,
) {
  if (config.config.tooltip) {
    config.config.tooltip.formatter = makeHeatmapTooltipFormatter(
      yAxisLabels,
      isPercentValue,
      isDurationValue,
    ) as TooltipFormatterCallbackFunction;
    config.config.tooltip.useHTML = true;
  }
}

export const HeatmapChart = ({
  chartSchema,
  chartDefinition,
  i18n,
  onReady,
}: HeatmapChartProps) => {
  const config = new DefaultHeatmapChartConfig();
  const series = getSeries(chartSchema);
  const { xAxisLabels, yAxisLabels } = getLabels(chartSchema, i18n);
  const isPercentValue = isPercent(chartSchema);
  const isDurationValue = isDuration(chartSchema);

  setDataLabelFormatter(config, isPercentValue, isDurationValue);
  setColorAxis(config, chartSchema, chartDefinition);
  setTooltipFormatter(config, yAxisLabels, isPercentValue, isDurationValue);

  if (onReady) {
    onReady(series, xAxisLabels, yAxisLabels);
  }

  const chartOptions: Highcharts.Options = {
    ...config.config,
    chart: { type: 'heatmap' },
    xAxis: {
      categories: xAxisLabels,
    },
    yAxis: {
      categories: yAxisLabels,
      reversed: true,
    },
    series: [
      {
        type: 'heatmap',
        data: series,
      },
    ],
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ChartTitle chartSchema={chartSchema} chartDefinition={chartDefinition} />
      <div className="flex-1 min-h-0">
        <Chart highcharts={Highcharts} options={chartOptions} />
      </div>
    </div>
  );
};
